import React, { useEffect, useState } from "react";
import { ForgeNavBar } from "../universe/Universe";
import "../providers/providers.css";
import "./bench.css";

const API_BASE = "" || "";

function useKernelToken() {
  const [token, setToken] = useState(() => localStorage.getItem("forge:kernel_token") || "");
  const save = (t) => { localStorage.setItem("forge:kernel_token", t); setToken(t); };
  return [token, save];
}

async function apiJson(path, method, body, token) {
  const r = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `${method} ${path} → ${r.status}`);
  return data;
}
async function apiGet(path, token) {
  const r = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`GET ${path} → ${r.status}`);
  return r.json();
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" });
  } catch { return iso; }
}
function fmtCost(v) {
  if (v == null) return "—";
  if (v === 0) return "gratis";
  return `$${v.toFixed(2)}`;
}
function fmtCtx(v) {
  if (v == null) return "—";
  if (v >= 1_000_000) return `${(v/1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${Math.round(v/1000)}k`;
  return String(v);
}

export default function BenchPage() {
  const [token, saveToken] = useKernelToken();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const runBench = async () => {
    setRunning(true); setError(null);
    try {
      const r = await apiJson("/api/kernel/providers/bench", "POST", { only_enabled: false, parallel: true }, token);
      setRows(r.results || []);
      setSummary(r.summary || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const loadDecisions = async () => {
    try {
      const r = await apiGet("/api/kernel/providers/decisions?limit=8", token);
      setDecisions(r.decisions || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { runBench(); loadDecisions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!token) {
    return (
      <div className="fp-page">
        <ForgeNavBar current="/bench" />
        <div className="fp-container">
          <div className="fp-token-gate">
            <h2>🔐 FORGE Kernel Token</h2>
            <p>Serve il token per accedere alla pagina di benchmark.</p>
            <div className="row">
              <input
                type="password"
                placeholder="forge-founder-…"
                onChange={(e) => e.target.value.length > 20 && saveToken(e.target.value)}
                data-testid="fb-token-input"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fp-page">
      <ForgeNavBar current="/bench" />
      <div className="fp-container">
        <header className="fp-header">
          <div>
            <h1>📊 AI Bench</h1>
            <p>Test parallelo di tutti i provider. Verifica connessione, latenza, contesto e costo. Aria usa questi dati per scegliere in modo trasparente.</p>
          </div>
          <div className="fp-stats">
            <div><b>{summary?.ok_count ?? 0}</b><span>online</span></div>
            <div><b>{summary?.total ?? 0}</b><span>testati</span></div>
            <div><b>{summary?.avg_latency_ms ?? 0}</b><span>ms medi</span></div>
          </div>
        </header>

        <section className="fp-section">
          <div className="fp-section-title">
            <span>Risultati</span>
            <button
              className="fp-btn primary"
              onClick={runBench}
              disabled={running}
              data-testid="fb-benchmark-all"
            >
              {running ? "🔬 Benchmark in corso…" : "🚀 Benchmark tutti"}
            </button>
          </div>
          {error && <div className="fb-error">{error}</div>}
          <div className="fb-table" data-testid="fb-table">
            <div className="fb-thead">
              <div>Provider</div>
              <div>Stato</div>
              <div>Latenza</div>
              <div>Modello</div>
              <div>Context</div>
              <div>Costo (in / out)</div>
              <div>Ultimo uso</div>
              <div>Errori 24h</div>
            </div>
            {rows.map((r) => (
              <div className={`fb-trow ${r.ok ? "ok" : "ko"}`} key={r.name} data-testid={`fb-row-${r.name}`}>
                <div className="fb-cell-name">
                  <span className={`fp-dot ${r.ok ? "ok" : "off"}`} />
                  <div>
                    <b>{r.label}</b>
                    <code>{r.name}</code>
                  </div>
                </div>
                <div>
                  {r.ok
                    ? <span className="fb-status ok">✓ Online</span>
                    : <span className="fb-status ko" title={r.error}>✗ {r.error?.slice(0, 40) || "Offline"}</span>}
                </div>
                <div>{r.latency_ms != null ? `${r.latency_ms} ms` : "—"}</div>
                <div><code>{r.model || "—"}</code></div>
                <div>{fmtCtx(r.context_size)}</div>
                <div className="fb-cost">
                  <span>{fmtCost(r.cost_in_per_1m)}</span>
                  <span className="sep">/</span>
                  <span>{fmtCost(r.cost_out_per_1m)}</span>
                  <span className="unit">per 1M tok</span>
                </div>
                <div>{fmtDate(r.last_used_at)}</div>
                <div className={r.errors_24h > 0 ? "fb-errors" : ""}>{r.errors_24h ?? 0}</div>
              </div>
            ))}
            {rows.length === 0 && !running && (
              <div className="fb-empty">Nessun risultato — clicca &quot;Benchmark tutti&quot;</div>
            )}
          </div>
        </section>

        <section className="fp-section">
          <div className="fp-section-title"><span>Ultime decisioni di Aria</span></div>
          <div className="fb-decisions" data-testid="fb-decisions">
            {decisions.length === 0 && <div className="fb-empty">Nessuna decisione registrata ancora. Fai una domanda ad Aria e torna qui.</div>}
            {decisions.map((d) => (
              <div className="fb-decision" key={d.id}>
                <div className="fb-decision-head">
                  <b>{d.provider}/{d.model}</b>
                  <span className="fb-task">{d.task_type || "—"}</span>
                  <span className="fb-dt">{fmtDate(d.created_at)}</span>
                  <span className="fb-lat">{d.duration_ms} ms</span>
                  {d.fallback_used && <span className="fb-fallback">fallback</span>}
                </div>
                <div className="fb-decision-reason">{d.reason}</div>
                {d.provider_scores?.length > 0 && (
                  <div className="fb-scores">
                    {d.provider_scores.slice(0, 8).map((s, i) => (
                      <span
                        key={i}
                        className={`fb-score ${s.chosen ? "chosen" : (s.available ? "avail" : "off")}`}
                        title={s.note}
                      >
                        {s.chosen ? "✓" : (s.available ? "◦" : "✗")} {s.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
